import { getScreenById } from "@/app/screenRegistry";
import { iconForNavGroup } from "@/app/nav/navIcons";
import type { OpsNavGroup, OpsNavLeaf } from "@/app/nav/types";
import type { ApiNavTreeResponse } from "@tm-food/api-contract";

const omittedLogged = new Set<string>();

const registryNavLabelIds = new Set([
  "SCR-ENG-PRODUCT",
  "SCR-ENG-PRODUCT-TYPE",
  "SCR-ENG-TECHNICAL-INFO",
  "SCR-ENG-PACK-FORMAT",
  "SCR-ENG-OP-TEMPLATE",
  "SCR-ENG-SPEC",
  "SCR-ENG-QCPLAN",
  "SCR-PLN-RESERVE",
  "SCR-MES-ISSUE",
  "SCR-WMS-RESERVATION",
  "SCR-MDM-ITEM",
  "SCR-BI-PROD",
  "SCR-BI-QC",
  "SCR-BI-WH",
]);

function registryNavLabel(screenId: string) {
  return registryNavLabelIds.has(screenId);
}

/** Thông tin kỹ thuật đứng trước danh mục quy cách và danh mục công đoạn. */
function placeTechnicalInfoBeforeCatalogs(children: OpsNavLeaf[]) {
  const from = children.findIndex((item) => item.screenId === "SCR-ENG-TECHNICAL-INFO");
  if (from < 0) return;
  const anchor = children.findIndex(
    (item) => item.screenId === "SCR-ENG-PACK-FORMAT" || item.screenId === "SCR-ENG-OP-TEMPLATE",
  );
  if (anchor < 0 || from < anchor) return;
  const [item] = children.splice(from, 1);
  const insertAt = children.findIndex(
    (row) => row.screenId === "SCR-ENG-PACK-FORMAT" || row.screenId === "SCR-ENG-OP-TEMPLATE",
  );
  if (item && insertAt >= 0) children.splice(insertAt, 0, item);
}

function logOmitOnce(screenId: string, reason: string) {
  if (omittedLogged.has(screenId)) return;
  omittedLogged.add(screenId);
  console.warn("[nav] omit screenId", { screenId, reason });
}

/**
 * Bind BE runtime nav → Ops sidebar model via screenRegistry (path / shell).
 * Unknown or non-ops screenIds are omitted (catalog drift).
 */
export function resolveApiNav(tree: ApiNavTreeResponse): OpsNavGroup[] {
  const groups: OpsNavGroup[] = [];

  for (const g of [...tree.groups].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const children: OpsNavLeaf[] = [];
    for (const item of [...g.items].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (item.placement && item.placement !== "sidebar") continue;
      const screen = getScreenById(item.screenId);
      if (!screen) {
        logOmitOnce(item.screenId, "not_in_screenRegistry");
        continue;
      }
      if (screen.shell !== "ops") {
        logOmitOnce(item.screenId, "shell_mismatch");
        continue;
      }
      children.push({
        screenId: item.screenId,
        path: screen.path,
        label: registryNavLabel(item.screenId) ? screen.label : item.label || screen.label,
      });
    }
    placeTechnicalInfoBeforeCatalogs(children);
    if (children.length === 0) continue;
    groups.push({
      id: g.code,
      label: g.label,
      icon: iconForNavGroup(g.iconKey, g.label),
      children,
    });
  }

  return groups;
}
